import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { PlanService, Plan } from '../../core/services/plan.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { ConfirmModalService } from 'src/app/core/services/confirm-modal.service';

@Component({
  selector: 'app-plans',
  templateUrl: './plans.component.html',
  styleUrl: './plans.component.scss'
})
export class PlansComponent implements OnInit {

  plans: Plan[] = [];
  loading = false;
  error = '';
  successMessage = '';

  planForm: UntypedFormGroup;
  modalRef: NgbModalRef;
  editingPlan: Plan | null = null;
  isEditMode = false;

  breadCrumbItems: Array<{}>;

  constructor(
    private planService: PlanService,
    private modalService: NgbModal,
    private fb: UntypedFormBuilder,
    private toastService: ToastService,
    private confirmModalService: ConfirmModalService
  ) {
    this.breadCrumbItems = [{ label: 'AVSS eSign' }, { label: 'Plans', active: true }];
  }

  ngOnInit(): void {
    this.initForm();
    this.loadPlans();
  }

  initForm() {
    this.planForm = this.fb.group({
      name: ['', [
        Validators.required,
        Validators.pattern(/^(?=.*[A-Za-z])[A-Za-z ]+$/)
      ]],
      price: [0, [
        Validators.required,
        Validators.min(1),
        Validators.max(1000000),
        Validators.pattern('^[0-9]+$')
      ]],
      documents: [0, [
        Validators.required,
        Validators.min(1),
        Validators.max(500000),
        Validators.pattern('^[0-9]+$')
      ]],
      users: [0, [
        Validators.required,
        Validators.min(1),
        Validators.max(100000),
        Validators.pattern('^[0-9]+$')
      ]],
      features: [''],
      description: [''],
      isActive: [true]
    });
  }

  loadPlans() {
    this.loading = true;
    this.error = '';
    this.planService.getPlans().subscribe(
      (data: Plan[]) => {
        this.plans = data;
        this.loading = false;
      },
      () => {
        this.loading = false;
      }
    );
  }

  openCreateModal(content: any) {
    this.isEditMode = false;
    this.editingPlan = null;
    this.planForm.reset({ price: 0, documents: 0, users: 0, isActive: true });
    this.error = '';
    this.successMessage = '';
    this.modalRef = this.modalService.open(content, { size: 'lg' });
  }

  openEditModal(plan: Plan, content: any) {
    this.isEditMode = true;
    this.editingPlan = plan;
    this.features = plan?.features;
    const featuresStr = Array.isArray(plan.features) ? plan.features.join(', ') : '';
    this.planForm.patchValue({
      name: plan.name,
      price: plan.price,
      documents: plan.documents,
      users: plan.users,
      features: featuresStr,
      description: plan.description,
      isActive: plan.isActive
    });
    this.error = '';
    this.successMessage = '';
    this.modalRef = this.modalService.open(content, { size: 'lg' });
  }

  savePlan() {
    this.planForm.markAllAsTouched();
    if (this.planForm.invalid) {
      const message = 'Please fill all required fields correctly.';
      this.toastService.showToaster("Form Validation", message, "info");
      return;
    }

    const formValue = this.planForm.value;
    const payload: Partial<Plan> = {
      name: formValue.name,
      price: formValue.price,
      documents: formValue.documents,
      users: formValue.users,
      description: formValue.description,
      isActive: formValue.isActive,
      features: formValue.features ? formValue.features.split(',').map((f: string) => f.trim()) : []
    };

    if (this.isEditMode && this.editingPlan) {
      this.planService.updatePlan(this.editingPlan._id, payload).subscribe(
        (updated: Plan) => {
          const idx = this.plans.findIndex(p => p._id === updated._id);
          if (idx > -1) {
            this.plans[idx] = updated;
          }
          this.features = [];
          this.successMessage = 'Plan updated successfully!';
          setTimeout(() => {
            this.modalRef.close();
          }, 1000);
        },
        () => { }
      );
    } else {
      this.planService.createPlan(payload).subscribe(
        (newPlan: Plan) => {
          this.plans.push(newPlan);
          this.features = [];
          this.toastService.showToaster("Plan Created", 'Plan created successfully!', "success");
          setTimeout(() => {
            this.modalRef.close();
          }, 1000);
        },
        () => { }
      );
    }
  }

  updateStatus(event: Event, plan: Plan) {
  const input = event.target as HTMLInputElement;
  const isChecked = input.checked;

  // switch ko old status par wapas kar do jab tak confirm na ho
  input.checked = !!plan.isActive;

  if (plan.isActive === isChecked) {
    return;
  }

  const action = isChecked ? 'activate' : 'deactivate';

  this.confirmModalService.open(
    'Plan Status',
    `Are you sure you want to ${action} the plan "${plan.name}"?`,
    () => {
      this.confirmUpdateStatus(plan, isChecked);
    },
    isChecked ? 'Activate' : 'Deactivate',
    'Cancel'
  );
}
confirmUpdateStatus(plan: Plan, isChecked: boolean) {
  this.loading = true;

  this.planService.updateStatus(plan._id, isChecked).subscribe(
    (updated: Plan) => {
      const idx = this.plans.findIndex(p => p._id === updated._id);

      if (idx > -1) {
        this.plans[idx] = updated;
      }

      this.loading = false;

      const status = isChecked ? 'activated' : 'deactivated';
      const title = isChecked ? 'Plan Activated' : 'Plan Deactivated';
      const message = `Plan "${plan.name}" ${status} successfully!`;

      this.toastService.showToaster(title, message, 'success');

      setTimeout(() => {
        this.successMessage = '';
      }, 3000);
    },
    () => {
      this.loading = false;
    }
  );
}

  closeModal() {
    if (this.modalRef) {
      this.modalRef.dismiss();
    }
  }

  preventInvalidInput(event: KeyboardEvent) {
    if (['e', 'E', '+', '-', '.'].includes(event.key)) {
      event.preventDefault();
    }
  }

  preventInvalidNameInput(event: KeyboardEvent) {
    if (event.key.length === 1 && !/[A-Za-z ]/.test(event.key)) {
      event.preventDefault();
    }
  }

  charCount: number = 0;
  features: string[] = [];
  featureInput: string = '';
  featureCount: number = 0;
  featureError: string = '';

  onDescriptionInput() {
    const text = this.planForm.get('description')?.value || '';
    this.charCount = text.length;
  }

  onFeatureInput() {
    const value = this.featureInput.trim();
    this.featureCount = value.length;
  }

  addFeature() {
    const value = this.featureInput.trim();
    if (this.features.length >= 10) {
      this.featureError = 'Maximum 10 features allowed.';
      return;
    }
    this.features.push(value);
    this.featureInput = '';
    this.updateFormControl();
    this.featureCount = 0;
  }

  removeFeature(index: number) {
    this.features.splice(index, 1);
    this.updateFormControl();
    this.featureError = '';
  }

  updateFormControl() {
    const value = this.features.join(', ');
    this.planForm.get('features')?.setValue(value);
  }

}
